import { router } from "@inertiajs/react";
import { Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import PurchaseRequestPreviewCard from "@/components/purchase-request-preview-card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { usePurchaseRequestSearch } from "@/hooks/use-purchase-request-search";
import {
  deliveryNotePath,
  deliveryNotePreviewPurchaseRequestPath,
  invoicePath,
  searchPurchaseRequestsPath,
} from "@/routes";
import type { PurchaseRequestPreview } from "@/types";

type Mode = "form" | "show";

interface PurchaseRequestPickerWithPreviewProps {
  value: number | null;
  onChange?: (value: number | null) => void; // usado en formularios (upload)
  disabled?: boolean;
  placeholder?: string;
  supplierId?: number; // si está, filtra por proveedor
  mode?: Mode; // 'form' | 'show'
  resource?: { type: "delivery_note" | "invoice"; id: number }; // requerido en modo show
  onBeforeChange?: (newValue: number | null) => boolean | Promise<boolean>; // interceptor for show mode
}

interface PurchaseRequestSearchResult {
  id: number;
  label: string;
  request_number: string;
  status: string;
  sector_name?: string;
}

const MAX_LABEL_LENGTH = 60;

// Simple formatting: show label with sector, basic truncation if needed
function formatOption(item: PurchaseRequestSearchResult): AsyncOption {
  const baseLabel = item.label?.trim() || `SC #${item.request_number || item.id}`;
  const fullLabel = item.sector_name
    ? `${baseLabel} - ${item.sector_name}`
    : baseLabel;

  const displayLabel = fullLabel.length > MAX_LABEL_LENGTH
    ? `${fullLabel.slice(0, MAX_LABEL_LENGTH)}…`
    : fullLabel;

  return {
    value: item.id,
    label: displayLabel,
    fullLabel,
  };
}

export default function PurchaseRequestPickerWithPreview({
  value,
  onChange,
  disabled = false,
  placeholder = "Buscar solicitud…",
  supplierId,
  mode = "form",
  resource,
  onBeforeChange,
}: PurchaseRequestPickerWithPreviewProps) {
  const [selectedOption, setSelectedOption] = useState<AsyncOption | null>(null);
  const [preview, setPreview] = useState<PurchaseRequestPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const asyncLoader = usePurchaseRequestSearch(supplierId);

  // In form mode, check URL params for initial purchase_request_id and notify parent
  useEffect(() => {
    if (mode !== "form" || value !== null || !onChange) return;

    const urlParams = new URLSearchParams(window.location.search);
    const purchaseRequestIdFromUrl = urlParams.get('purchase_request_id');
    
    if (purchaseRequestIdFromUrl) {
      const id = Number(purchaseRequestIdFromUrl);
      if (!isNaN(id) && id > 0) {
        // Notify parent of the URL param value
        onChange(id);
      }
    }
  }, [mode, value, onChange]);

  const hasChanges = useMemo(() => {
    return (selectedOption?.value ?? null) !== (value ?? null);
  }, [selectedOption, value]);

  // Cargar opción inicial si viene un value (para mostrar label)
  useEffect(() => {
    if (value == null) {
      setSelectedOption(null);
      setPreview(null);
      return;
    }

    if (selectedOption && Number(selectedOption.value) === value) {
      return;
    }

    const controller = new AbortController();
    const loadInitialOption = async () => {
      try {
        // Búsqueda global por id para obtener el label (fallback simple si no aparece)
        const params = new URLSearchParams({ global: "1", term: String(value) });
        const resp = await fetch(`${searchPurchaseRequestsPath()}?${params.toString()}`, { signal: controller.signal });
        if (resp.ok) {
          const items = (await resp.json()) as PurchaseRequestSearchResult[];
          const match = items.find((i) => i.id === value);
          if (match) {
            setSelectedOption(formatOption(match));
            return;
          }
        }
        // Fallback: mostrar SC #id para no romper la UI; la vista previa enriquecerá luego
        setSelectedOption({ value, label: `SC #${value}` });
      } catch (error) {
        if ((error as Error).name === "AbortError") return;
        console.error("Error loading initial option", error);
        // Fallback en caso de error también
        setSelectedOption({ value, label: `SC #${value}` });
      }
    };
    void loadInitialOption();
    return () => controller.abort();
  }, [value]);

  // Cargar preview cuando se selecciona una opción
  useEffect(() => {
    if (!selectedOption) {
      setPreview(null);
      return;
    }

    const loadPreview = async () => {
      setLoadingPreview(true);
      try {
        const url = deliveryNotePreviewPurchaseRequestPath(selectedOption.value);
        const response = await fetch(url);
        if (!response.ok) throw new Error("Error al cargar la vista previa");
        const data = (await response.json()) as PurchaseRequestPreview;
        setPreview(data);
      } catch (error) {
        console.error("Error loading preview:", error);
      } finally {
        setLoadingPreview(false);
      }
    };

    void loadPreview();
  }, [selectedOption]);

  const loadOptions = useCallback(
    async (term: string): Promise<AsyncOption[]> => {
      if (disabled) return [];
      return await asyncLoader(term);
    },
    [disabled, asyncLoader],
  );

  // Unified handler for option changes
  const handleOptionChange = useCallback(
    (option: AsyncOption | null) => {
      setSelectedOption(option);
      if (!option) {
        setPreview(null);
      }

      // In form mode, propagate to parent immediately
      if (mode === "form" && onChange) {
        onChange(option ? Number(option.value) : null);
      }
    },
    [mode, onChange],
  );

  const handleSave = useCallback(async () => {
    if (mode !== "show" || !resource) return;

    // Call onBeforeChange interceptor if provided
    if (onBeforeChange) {
      const shouldProceed = await onBeforeChange(selectedOption?.value ? Number(selectedOption.value) : null);
      if (!shouldProceed) {
        return; // Cancel save
      }
    }

    setSaving(true);
    const url = resource.type === "delivery_note" ? deliveryNotePath(resource.id) : invoicePath(resource.id);
    router.patch(
      url,
      { purchase_request_id: selectedOption?.value ?? null },
      {
        preserveScroll: true,
        onSuccess: () => {
          toast.success(selectedOption ? "Solicitud vinculada correctamente" : "Vínculo removido correctamente");
        },
        onError: (errors) => {
          const message = (Object.values(errors)[0] as string | undefined) ?? "No se pudo actualizar";
          toast.error(message);
        },
        onFinish: () => setSaving(false),
      },
    );
  }, [mode, resource, selectedOption, onBeforeChange]);

  const accordionTitle = selectedOption
    ? `SC #${preview?.request_number ?? selectedOption.value} - Ver detalle`
    : "Vincular a Solicitud de Compra";

  const content = (
    <>
      {/* Selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Solicitud de Compra</label>
        <div className="flex gap-2">
          <div className="flex-1 min-w-0 overflow-hidden">
            <AsyncCombobox
              value={selectedOption}
              onChange={handleOptionChange}
              loadOptions={loadOptions}
              placeholder={placeholder}
              buttonClassName={disabled ? "opacity-60 pointer-events-none" : undefined}
            />
          </div>
          {selectedOption && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleOptionChange(null)}
              title="Quitar vínculo"
              disabled={disabled || saving}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Vista previa */}
      {loadingPreview && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Cargando vista previa…
        </div>
      )}

      {preview && !loadingPreview && (
        <PurchaseRequestPreviewCard preview={preview} defaultExpanded={mode === "form"} />
      )}

      {/* Acciones en modo show */}
      {mode === "show" && (
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={handleSave} disabled={disabled || saving || !hasChanges}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              "Vincular"
            )}
          </Button>
        </div>
      )}
    </>
  );

  // En modo form, mostrar sin accordion
  if (mode === "form") {
    return (
      <div className="bg-stone-50 border border-stone-200 rounded-md p-4 space-y-3">
        {content}
      </div>
    );
  }

  // En modo show, envolver en accordion
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-md p-4">
      <Accordion type="single" collapsible>
        <AccordionItem value="purchase-request-link">
          <AccordionTrigger className="text-sm font-medium">{accordionTitle}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-2">{content}</div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
